import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    clearAppointmentsAndSlotsByParticipantAndDateRange,
    clearAppointmentsAndSlotsByParticipantAndDateRangeMetaData,
    clearAppointmentsAndSlotsByUserAndDateRange,
    clearUserEventsByUserAndDateRange,
    searchAppointmentsAndSlotsByParticipantAndDateRange,
    searchAppointmentsAndSlotsByUserAndDateRange,
    searchUserEventsByUserAndDateRange,
    setOpenAppointmentSchedulerPanel,
    setParticipantsCurrentAppointment,
    updateParticipantCalendarAppointment,
    updateParticipantCalendarAppointmentSlot
} from '../../../store/calendarService';
import {
    clearCourseModulesByUserAndDateRange,
    clearModulesByParticipantAndDateRange,
    clearModulesByParticipantAndDateRangeMetaData,
    searchCourseModulesByUserAndDateRange,
    searchModulesByParticipantAndDateRange
} from '../../../store/courseService';
import {
    loadAppointmentTypeDetailsByContractId,
    loadLocationsByServiceId,
    loadServiceDetailsByService
} from '../../../store/directusService';
import { selectCurrentParticipantsName } from '../../../store/participantSelectors';
import { clearUsersBySearch } from '../../../store/userService';
import {
    POLARIS_BLACK,
    POLARIS_GREY,
    POLARIS_ROYAL_BLUE,
    POLARIS_WHITE
} from '../../../themes/theme';
import { todayDate } from '../../../utils/dateFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { hasRole } from '../../../utils/userRoles';
import Calendar from '../calendar/Calendar';
import AdviserSelect from '../components/AdviserSelect';
import {
    acceptedRoles,
    configCourseEventContent,
    configEventContent,
    configUserEventContent,
    formatFullCalendarTimes,
    getBusinessHours,
    getServiceIds,
    group2,
    group3,
    mapColors,
    mapCommonParams,
    mapUserEventColors
} from '../utils/calendarUtils';

import ParticipantAppointmentCreate from './ParticipantAppointmentCreate';
import ParticipantAppointmentEdit from './ParticipantAppointmentEdit';
import ParticipantAppointmentSlotsEdit from './ParticipantAppointmentSlotsEdit';
import ParticipantAppointmentSlotsView from './ParticipantAppointmentSlotsView';
import ParticipantAppointmentView from './ParticipantAppointmentView';

import classes from '../../../app.module.css';

const _DOUBLE_CLICK = 300;
let userClickCount = 0;

const ParticipantCalendar = () => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [loggedInAdviser, setLoggedInAdviser] = useState({});
    const [range, setRange] = useState({
        dateFrom: todayDate(),
        dateTo: todayDate()
    });
    const [events, setEvents] = useState([]);
    const [userEvents, setUserEvents] = useState([]);

    const [hiddenEvents, setHiddenEvents] = useState([]);
    const [newEvent, setNewEvent] = useState({});
    const [currentEvent, setCurrentEvent] = useState({});
    const [adviser, setAdviser] = useState({});
    const [activeParticipant, setActiveParticipant] = useState({});

    const [currentParticipantsModules, setCurrentParticipantsModules] = useState([]);
    const [currentParticipantsAppointments, setCurrentParticipantsAppointments] = useState([]);

    const [currentUsersModules, setCurrentUsersModules] = useState([]);
    const [currentUsersAppointments, setCurrentUsersAppointments] = useState([]);
    const [currentUserEvents, setCurrentUserEvents] = useState([]);

    const [viewOnlyAppointment, setViewOnlyAppointment] = useState(true);
    const [viewOnlySlots, setViewOnlySlots] = useState(true);
    const [canEditAppointment, setCanEditAppointment] = useState(false);
    const [canEditSlots, setCanEditSlots] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [isActionsDisabled, setIsActionsDisabled] = useState(true);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const usersByLoggedInUsersServiceIds = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIds
    );
    const users = useSelector((state) => state.entities.userService.usersBySearch);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // PT appointments/slots
    const participantAppointments = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByParticipantAndDateRange
    );
    const participantAppointmentsMetaData = useSelector(
        (state) =>
            state.entities.calendarService.appointmentsAndSlotsByParticipantAndDateRangeMetaData
    );
    const loadingParticipantAppointments = useSelector(
        (state) =>
            state.entities.calendarService.loadingAppointmentsAndSlotsByParticipantAndDateRange
    );
    const loadedAppointmentUpdate = useSelector(
        (state) => state.entities.calendarService.loadedAppointmentUpdate
    );
    const appointmentsCreated = useSelector(
        (state) => state.entities.calendarService.appointmentsCreated
    );

    // PT modules
    const participantsModules = useSelector(
        (state) => state.entities.courseService.modulesByParticipantAndDateRange
    );
    const loadingParticipantsModules = useSelector(
        (state) => state.entities.courseService.loadingModulesByParticipantAndDateRange
    );
    const participantsModulesMetaData = useSelector(
        (state) => state.entities.courseService.modulesByParticipantAndDateRangeMetaData
    );

    // hidden events - user's appointments
    const usersAppointments = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRange
    );
    const usersAppointmentsMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRangeMetaData
    );
    const loadingUsersAppointments = useSelector(
        (state) => state.entities.calendarService.loadingUsersAppointmentsAndSlots
    );

    // hidden events - user's course modules
    const usersModules = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRange
    );
    const loadingUsersModules = useSelector(
        (state) => state.entities.courseService.loadingCourseModulesByUserAndDateRange
    );
    const usersModulesMetaData = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRangeMetaData
    );

    // user events
    const userEventsByUser = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRange
    );
    const userEventsMetaData = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRangeMetaData
    );
    const loadingUserEvents = useSelector(
        (state) => state.entities.calendarService.loadingUserEventsByUserAndDateRange
    );

    const participantsName = useSelector(selectCurrentParticipantsName);
    const currentAppointment = useSelector(
        (state) => state.entities.calendarService.participantsCurrentAppointment
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );

    // directus
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const appointmentTypesByContractId = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetailsByContractId
    );
    const locationsByServiceId = useSelector(
        (state) => state.entities.directusService.locationsByServiceId
    );

    // CONFIG FNS
    const configParticipantEvents = () => {
        const businessHours = getBusinessHours();
        let appEvents;
        const newCreatedAppointments =
            activeParticipant?.id in appointmentsCreated
                ? appointmentsCreated[activeParticipant.id]
                : [];
        const newAppointments = [...currentParticipantsAppointments, ...newCreatedAppointments];
        if (newAppointments.length < 1) appEvents = [{}];
        else
            appEvents = newAppointments.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { slots, filledSlots, displayCommLinks } = configEventContent(el);
                const { color, textColor } = mapColors(el.id, currentAppointment?.id);
                const editable = el.id === currentAppointment?.id;
                return {
                    id: el.id,
                    eventType: 'participant',
                    hasLink: true,
                    slots,
                    filledSlots,
                    location: el.location,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    participantEntry: activeParticipant,
                    participant: !slots ? participantsName : '',
                    appointmentType: el.type,
                    additionalDetails: el.additionalDetails,
                    date,
                    start,
                    end,
                    color,
                    textColor,
                    editable,
                    displayCommLinks,
                    disableCommsEmail: !activeParticipant?.emailAddress,
                    constraint: businessHours
                };
            });

        let moduleEvents;
        if (currentParticipantsModules?.length < 1) moduleEvents = [];
        else {
            moduleEvents = currentParticipantsModules?.map((el) => {
                // course modules are non interactable.
                const { date, start, end } = mapCommonParams(el);
                const content = configCourseEventContent(el);
                return {
                    eventType: 'courseModule',
                    className: 'non-interactive-events',
                    content,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    editable: false,
                    date,
                    start,
                    end,
                    color: POLARIS_ROYAL_BLUE,
                    textColor: POLARIS_WHITE
                };
            });
        }
        setEvents([...appEvents, ...moduleEvents]);
        if (appEvents?.length > 0) {
            const cur = appEvents?.find((el) => el.id === currentAppointment?.id);
            if (cur) setCurrentEvent(cur);
        }
    };

    const configUserHiddenEvents = () => {
        const filteredModules = currentUsersModules?.filter(
            (el) => !el.participants?.some((entry) => entry.participantId === activeParticipant.id)
        );
        const filteredAppointments = currentUsersAppointments
            ?.filter((el) => el.participantId !== activeParticipant.id)
            ?.filter((el) => !el.participantIds?.includes(activeParticipant.id));

        const allUserAppointments = [...filteredAppointments, ...filteredModules];
        let events;
        if (allUserAppointments?.length < 1) events = [];
        else
            events = allUserAppointments?.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                return {
                    eventType: 'hidden',
                    className: 'busy-events',
                    content: `${start.slice(-5)} - ${end.slice(-5)} Busy`,
                    editable: false,
                    color: POLARIS_GREY,
                    textColor: POLARIS_BLACK,
                    date,
                    start,
                    end
                };
            });
        setHiddenEvents(events);
    };

    const configUserEvents = () => {
        if (!Array.isArray(currentUserEvents)) return;
        let events;
        if (currentUserEvents.length < 1) events = [];
        else
            events = currentUserEvents.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { color, textColor } = mapUserEventColors(el.id);
                const { displayOccurrence, content } = configUserEventContent(el);
                return {
                    id: el.id,
                    eventType: 'userEvent',
                    className: 'non-interactive-events',
                    content,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    editable: false,
                    repeat: el.repeat,
                    repeatUntilDate: el.repeatUntilDate,
                    displayOccurrence
                };
            });
        setUserEvents(events);
    };

    // USEEFFECTS
    useEffect(() => {
        dispatch(clearUsersBySearch());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!loggedInUser || !loggedInUser?.id || !usersByLoggedInUsersServiceIds) return;
        const user =
            usersByLoggedInUsersServiceIds.find((el) => el.id === loggedInUser.id) || loggedInUser;
        setAdviser(user);
        setLoggedInAdviser(user);
    }, [loggedInUser.id, usersByLoggedInUsersServiceIds]);

    useEffect(() => {
        if ('id' in activeParticipant) fetchParticipantData(activeParticipant.id);
    }, [range, activeParticipant]);

    useEffect(() => {
        if (roles.length < 1) return;
        if ('id' in activeParticipant && currentParticipant?.id !== activeParticipant.id) {
            navigate('/edit_participant');
        } else {
            setActiveParticipant(currentParticipant);

            const isClosed = currentParticipant.status === 'Closed';
            const restrictOnClosed = isClosed && !hasRole(EDIT_ON_CLOSED_ROLES, roles);

            setIsActionsDisabled(!hasRole(acceptedRoles, roles));
            setViewOnlyAppointment(
                (hasRole(group2, roles) && restrictOnClosed) ||
                    (hasRole(group3, roles) && !hasRole(acceptedRoles, roles))
            );
            setViewOnlySlots(
                (hasRole(group2, roles) && restrictOnClosed) ||
                    (hasRole(group3, roles) && !hasRole(acceptedRoles, roles))
            );
            setCanEditSlots(hasRole(acceptedRoles, roles) && !restrictOnClosed);
            setCanEditAppointment(hasRole(acceptedRoles, roles) && !restrictOnClosed);
            setCanCreate(hasRole(acceptedRoles, roles) && !restrictOnClosed);
        }
    }, [currentParticipant, roles]);

    useEffect(() => {
        if (Object.keys(activeParticipant)?.length < 1) return;
        const { serviceId, contractId } = activeParticipant;
        if (!(serviceId in serviceDetailsById)) dispatch(loadServiceDetailsByService([serviceId]));
        if (!(contractId in appointmentTypesByContractId)) {
            dispatch(loadAppointmentTypeDetailsByContractId([contractId]));
        }
        if (!(serviceId in locationsByServiceId)) {
            dispatch(loadLocationsByServiceId([serviceId]));
        }
    }, [activeParticipant]);

    // Participants Modules & Appointments - page + 1
    useEffect(() => {
        if (
            !loadingParticipantsModules &&
            !!participantsModules[`${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`]
                ?.length &&
            'number' in participantsModulesMetaData &&
            !participantsModulesMetaData.last
        ) {
            dispatch(
                searchModulesByParticipantAndDateRange(
                    {
                        participantId: activeParticipant?.id,
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    participantsModulesMetaData.number + 1
                )
            );
        } else if (
            !loadingParticipantAppointments &&
            !!participantAppointments[`${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`]
                ?.length &&
            'number' in participantAppointmentsMetaData &&
            !participantAppointmentsMetaData.last
        ) {
            dispatch(
                searchAppointmentsAndSlotsByParticipantAndDateRange(
                    {
                        participantIds: [activeParticipant.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    participantAppointmentsMetaData.number + 1
                )
            );
        } else if (
            !loadingParticipantAppointments &&
            !loadingParticipantsModules &&
            participantsModulesMetaData?.last &&
            participantAppointmentsMetaData?.last
        ) {
            dispatch(clearAppointmentsAndSlotsByParticipantAndDateRangeMetaData());
            dispatch(clearModulesByParticipantAndDateRangeMetaData());

            setCurrentParticipantsModules(
                participantsModules[`${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`] ||
                    []
            );
            setCurrentParticipantsAppointments(
                participantAppointments[
                    `${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`
                ] || []
            );

            if ('id' in adviser) fetchAdviserData(adviser.id);
        }
    }, [
        participantsModules[`${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`],
        participantsModulesMetaData,
        loadingParticipantsModules,
        participantAppointments[`${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`],
        participantAppointmentsMetaData,
        loadingParticipantAppointments
    ]);

    useEffect(() => {
        if (loadedAppointmentUpdate) {
            setCurrentParticipantsAppointments(
                participantAppointments[
                    `${activeParticipant.id}_${range.dateFrom}_${range.dateTo}`
                ] || []
            );
        }
    }, [loadedAppointmentUpdate]);

    useEffect(() => {
        configParticipantEvents();
    }, [
        currentParticipantsModules,
        currentParticipantsAppointments,
        appointmentsCreated[activeParticipant.id]
    ]);

    // User Events loading - page + 1
    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingUserEvents &&
            !!userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in userEventsMetaData &&
            !userEventsMetaData.last
        ) {
            dispatch(
                searchUserEventsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    userEventsMetaData.number + 1
                )
            );
        } else if (userEventsMetaData?.last) {
            setCurrentUserEvents(
                userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    }, [
        userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        loadingUserEvents,
        userEventsMetaData
    ]);

    useEffect(() => {
        configUserEvents();
    }, [currentUserEvents]);

    // Users modules loading - page + 1
    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingUsersModules &&
            !!usersModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in usersModulesMetaData &&
            !usersModulesMetaData.last
        ) {
            dispatch(
                searchCourseModulesByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    usersModulesMetaData.number + 1
                )
            );
        } else if (usersModulesMetaData.last)
            setCurrentUsersModules(
                usersModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
    }, [
        usersModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        usersModulesMetaData,
        loadingUsersModules
    ]);

    // Users appointments loading - page + 1
    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingUsersAppointments &&
            !!usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in usersAppointmentsMetaData &&
            !usersAppointmentsMetaData.last
        ) {
            dispatch(
                searchAppointmentsAndSlotsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    usersAppointmentsMetaData.number + 1
                )
            );
        } else if (usersAppointmentsMetaData?.last) {
            setCurrentUsersAppointments(
                usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    }, [
        loadingUsersAppointments,
        usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        usersAppointmentsMetaData
    ]);

    useEffect(() => {
        configUserHiddenEvents();
    }, [currentUsersModules, currentUsersAppointments]);

    useEffect(() => {
        if (!currentAppointment?.id) setCurrentEvent({});
        configParticipantEvents();
    }, [currentAppointment]);

    // HELPER FNS
    const fetchAdviserData = (adviserId) => {
        if (!adviserId) return;
        const params = {
            userIds: [adviserId],
            dateFrom: range.dateFrom,
            dateTo: range.dateTo
        };

        if (
            !(`${adviserId}_${range.dateFrom}_${range.dateTo}` in usersAppointments) &&
            !loadingUsersAppointments
        ) {
            dispatch(searchAppointmentsAndSlotsByUserAndDateRange(params, 0));
        } else {
            setCurrentUsersAppointments(
                usersAppointments[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }

        if (
            !(`${adviserId}_${range.dateFrom}_${range.dateTo}` in usersModules) &&
            !loadingUsersModules
        ) {
            dispatch(searchCourseModulesByUserAndDateRange(params, 0));
        } else {
            setCurrentUsersModules(
                usersModules[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }

        if (
            !(`${adviserId}_${range.dateFrom}_${range.dateTo}` in userEventsByUser) &&
            !loadingUserEvents
        ) {
            dispatch(searchUserEventsByUserAndDateRange(params, 0));
        } else {
            setCurrentUserEvents(
                userEventsByUser[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    };

    const fetchParticipantData = (participantId) => {
        if (!participantId) return;
        let participantModulesFetched = false;
        let participantAppointmentsFetched = false;

        if (
            !(`${participantId}_${range.dateFrom}_${range.dateTo}` in participantsModules) &&
            !loadingParticipantsModules
        ) {
            dispatch(
                searchModulesByParticipantAndDateRange(
                    {
                        participantId,
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    0
                )
            );
        } else {
            setCurrentParticipantsModules(
                participantsModules[`${participantId}_${range.dateFrom}_${range.dateTo}`] || []
            );
            participantModulesFetched = true;
        }

        if (
            !(`${participantId}_${range.dateFrom}_${range.dateTo}` in participantAppointments) &&
            !loadingParticipantAppointments
        ) {
            dispatch(
                searchAppointmentsAndSlotsByParticipantAndDateRange(
                    {
                        participantIds: [participantId],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    0
                )
            );
        } else {
            setCurrentParticipantsAppointments(
                participantAppointments[`${participantId}_${range.dateFrom}_${range.dateTo}`] || []
            );
            participantAppointmentsFetched = true;
        }

        if (participantModulesFetched && participantAppointmentsFetched) {
            if ('id' in adviser) fetchAdviserData(adviser.id);
        }
    };

    const cleanUp = () => {
        dispatch(clearAppointmentsAndSlotsByParticipantAndDateRange());
        dispatch(clearModulesByParticipantAndDateRange());
        dispatch(clearUserEventsByUserAndDateRange());
        dispatch(clearAppointmentsAndSlotsByUserAndDateRange());
        dispatch(clearCourseModulesByUserAndDateRange());
        dispatch(clearUsersBySearch());
    };

    // EVENT HANDLERS
    const onDragEditTimes = (time, update) => {
        if (update) {
            const { date, startTime, endTime } = formatFullCalendarTimes(time);
            const updatedAppointment = { ...currentAppointment, date, startTime, endTime };
            currentEvent.slots
                ? dispatch(
                      updateParticipantCalendarAppointmentSlot(
                          currentParticipant.id,
                          currentEvent.appointmentType,
                          updatedAppointment
                      )
                  )
                : dispatch(
                      updateParticipantCalendarAppointment(
                          currentParticipant.id,
                          currentEvent.appointmentType,
                          updatedAppointment
                      )
                  );
        }
        dispatch(setParticipantsCurrentAppointment({}));
    };

    const onTogglePanel = (open) => {
        dispatch(setOpenAppointmentSchedulerPanel(open));
    };

    const onSelectEvent = (id) => {
        setNewEvent({});
        if (id === currentEvent.id) {
            userClickCount++;
            const timeoutId = setTimeout(() => (userClickCount = 0), _DOUBLE_CLICK);
            if (userClickCount > 1) dispatch(setOpenAppointmentSchedulerPanel(true));
            return () => clearTimeout(timeoutId);
        } else if (id !== currentAppointment?.id) {
            const newAppointments =
                activeParticipant.id in appointmentsCreated
                    ? appointmentsCreated[activeParticipant.id]
                    : [];
            const selectedAppointment = [
                ...currentParticipantsAppointments,
                ...newAppointments
            ].find((el) => el.id === id);
            selectedAppointment && dispatch(setParticipantsCurrentAppointment(selectedAppointment));
        }
    };

    const onSelectSlot = (start, end) => {
        dispatch(setParticipantsCurrentAppointment({}));
        const { date, startTime, endTime } = formatFullCalendarTimes({ start, end });
        setNewEvent({ date, startTime, endTime });
        onTogglePanel(true);
    };

    const onGetCurrentTimeSpan = (dateFrom, dateTo, view) => {
        setRange({
            dateFrom,
            dateTo: view === 'timeGridDay' ? dateFrom : dateTo
        });
    };

    const onAdviserChange = (chosenId) => {
        if (!chosenId) {
            if (adviser.id !== loggedInUser.id) {
                setAdviser(loggedInUser);
            }
        } else if (chosenId && chosenId !== adviser.id) {
            const chosenAdviser = [...users, loggedInAdviser].find((el) => el.id === chosenId);
            setAdviser({
                ...chosenAdviser,
                contractIds: chosenAdviser?.contracts || chosenAdviser?.contractIds,
                serviceIds: getServiceIds(chosenAdviser)
            });
            fetchAdviserData(chosenId);
        }
    };

    const onNav = (event, link) => {
        if (link === 'participant') navigate('/edit_participant');
        else if (link === 'letter' || link === 'email') {
            const selectedAppointment =
                event._def.publicId === currentAppointment?.id
                    ? currentAppointment
                    : participantAppointments.find((el) => el.id === event._def.publicId);
            if (selectedAppointment) {
                if (link === 'email') {
                    navigate('/email_communication', {
                        state: {
                            status: selectedAppointment.attendances[0]?.status,
                            appointmentId: selectedAppointment.id
                        }
                    });
                } else if (link === 'letter') {
                    navigate('/letter_communication', {
                        state: {
                            status: selectedAppointment.attendances[0]?.status,
                            appointmentId: selectedAppointment.id
                        }
                    });
                }
            }
        }
    };

    // RENDER
    return (
        <div className={classes.container}>
            <div className="calendar-wrapper">
                <AdviserSelect
                    userId={adviser.id}
                    serviceIds={loggedInUser.serviceIds}
                    initialAdviser={loggedInAdviser}
                    onAdviserChange={onAdviserChange}
                    disabled={isActionsDisabled}
                    customClass="calendarToolbarAdviserSelect"
                />

                <Calendar
                    type="participantCalendar"
                    events={[...events, ...hiddenEvents, ...userEvents]}
                    currentEvent={currentEvent}
                    onDragEditTimes={onDragEditTimes}
                    onSelectSlot={onSelectSlot}
                    onSelectEvent={onSelectEvent}
                    isActionsDisabled={isActionsDisabled}
                    initialView="timeGridDay"
                    onGetCurrentTimeSpan={onGetCurrentTimeSpan}
                    onNav={onNav}
                />
                {openAppointmentSchedulerPanel &&
                    adviser?.id &&
                    canCreate &&
                    Object.keys(newEvent).length > 0 && (
                        <ParticipantAppointmentCreate
                            newAppointment={newEvent}
                            currentCalendarOwner={adviser}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser?.id &&
                    currentEvent.slots &&
                    viewOnlySlots && (
                        <ParticipantAppointmentSlotsView
                            currentEvent={currentAppointment}
                            currentLocation={currentEvent.location}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser?.id &&
                    !currentEvent.slots &&
                    viewOnlyAppointment &&
                    currentEvent?.participantEntry &&
                    Object.keys(newEvent).length < 1 && (
                        <ParticipantAppointmentView
                            currentEvent={currentAppointment}
                            currentLocation={currentEvent.location}
                            currentParticipantEntry={currentEvent.participantEntry}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser?.id &&
                    currentEvent.slots &&
                    canEditSlots &&
                    Object.keys(newEvent).length < 1 && (
                        <ParticipantAppointmentSlotsEdit currentEvent={currentAppointment} />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser?.id &&
                    !currentEvent.slots &&
                    canEditAppointment &&
                    currentEvent?.participantEntry &&
                    Object.keys(newEvent).length < 1 && (
                        <ParticipantAppointmentEdit
                            currentEvent={currentAppointment}
                            currentLocation={currentEvent.location}
                            currentParticipantEntry={currentEvent.participantEntry}
                        />
                    )}
            </div>
        </div>
    );
};

export default ParticipantCalendar;
